import React from 'react';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { GripVertical, Image, MessageSquare, MousePointer, Info, ImageIcon } from 'lucide-react';

interface HomeElement {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  enabled: boolean;
}

interface HomeElementsOrderManagerProps {
  elementOrder: string[];
  showLogo: boolean;
  showWelcomeMessage: boolean;
  showExhibitAccess: boolean;
  showVisitorInfo: boolean;
  showHomeImage: boolean;
  saving: boolean;
  onUpdateOrder: (newOrder: string[]) => Promise<void>;
  onUpdateSetting: (key: string, value: boolean) => Promise<void>;
}

function SortableElement({ element, onToggle, disabled }: { 
  element: HomeElement; 
  onToggle: (id: string, enabled: boolean) => void;
  disabled: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: element.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border"
    >
      <div {...attributes} {...listeners} className="cursor-grab hover:cursor-grabbing">
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
      
      <div className="flex items-center gap-2 text-muted-foreground">
        {element.icon}
      </div>
      
      <div className="flex-1">
        <div className="font-medium">{element.name}</div>
        <div className="text-sm text-muted-foreground">{element.description}</div>
      </div>
      
      <Switch
        checked={element.enabled}
        onCheckedChange={(enabled) => onToggle(element.id, enabled)}
        disabled={disabled}
      />
    </div>
  );
}

export function HomeElementsOrderManager({
  elementOrder,
  showLogo,
  showWelcomeMessage,
  showExhibitAccess,
  showVisitorInfo,
  showHomeImage,
  saving,
  onUpdateOrder,
  onUpdateSetting
}: HomeElementsOrderManagerProps) {
  const elementDefinitions = {
    logo: {
      id: 'logo',
      name: 'Museum Logo',
      icon: <Image className="h-4 w-4" />,
      description: 'Display the museum logo at the top',
      enabled: showLogo
    },
    welcomeMessage: {
      id: 'welcomeMessage',
      name: 'Welcome Message',
      icon: <MessageSquare className="h-4 w-4" />,
      description: 'Welcome text and museum information',
      enabled: showWelcomeMessage
    },
    exhibitAccess: {
      id: 'exhibitAccess',
      name: 'Exhibit Access Options',
      icon: <MousePointer className="h-4 w-4" />,
      description: 'Buttons for accessing exhibits (QR, number, etc.)',
      enabled: showExhibitAccess
    },
    visitorInfo: {
      id: 'visitorInfo',
      name: 'Visitor Information',
      icon: <Info className="h-4 w-4" />,
      description: 'Additional visitor information and links',
      enabled: showVisitorInfo
    },
    homeImage: {
      id: 'homeImage',
      name: 'Home Image',
      icon: <ImageIcon className="h-4 w-4" />,
      description: 'Display a featured image on the home page',
      enabled: showHomeImage
    }
  };

  const elements: HomeElement[] = elementOrder.map(id => elementDefinitions[id as keyof typeof elementDefinitions]).filter(Boolean);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = elements.findIndex(item => item.id === active.id);
      const newIndex = elements.findIndex(item => item.id === over?.id);
      
      const newOrder = [...elementOrder];
      const [movedItem] = newOrder.splice(oldIndex, 1);
      newOrder.splice(newIndex, 0, movedItem);
      
      onUpdateOrder(newOrder);
    }
  };

  const handleToggle = (elementId: string, enabled: boolean) => {
    const settingMap = {
      logo: 'showLogo',
      welcomeMessage: 'showWelcomeMessage',
      exhibitAccess: 'showExhibitAccess',
      visitorInfo: 'showVisitorInfo',
      homeImage: 'showHomeImage'
    };
    
    const settingKey = settingMap[elementId as keyof typeof settingMap];
    if (settingKey) {
      onUpdateSetting(settingKey, enabled);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Element Order & Visibility</CardTitle>
        <CardDescription>
          Drag to reorder elements and toggle their visibility on the home page
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={elements.map(e => e.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {elements.map((element) => (
                <SortableElement
                  key={element.id}
                  element={element}
                  onToggle={handleToggle}
                  disabled={saving}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </CardContent>
    </Card>
  );
}